import React, { useState, useEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import AddLinkRoundedIcon from "@mui/icons-material/AddLinkRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { QRCodeSVG } from "qrcode.react";
import { collection, query, where, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from "./firebaseConfig";
import ManageLinkDialog from "./ManageLinkDialog";

function LinkCard({ link, onCopy, isPremium, onDelete, onManage, onViewQR }) {
  const shortUrl = `${window.location.hostname}/${link.id}`;

  const createdAt = link.createdAt?.toDate?.();
  const dateStr = createdAt
    ? createdAt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <Card sx={{ bgcolor: "background.paper", borderRadius: 4, boxShadow: "none" }}>
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ overflow: "hidden", flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
              <Typography fontWeight={700} fontSize="0.95rem" noWrap color="primary.main">
                {shortUrl}
              </Typography>
              {link.isCustom && (
                <Chip
                  label="Custom"
                  size="small"
                  icon={<WorkspacePremiumRoundedIcon sx={{ fontSize: "0.7rem !important" }} />}
                  sx={{ height: 20, fontSize: "0.65rem", fontWeight: 600 }}
                  color="primary"
                  variant="outlined"
                />
              )}
              {link.folder && (
                <Chip
                  label={link.folder}
                  size="small"
                  icon={<FolderRoundedIcon sx={{ fontSize: "0.7rem !important" }} />}
                  sx={{ height: 20, fontSize: "0.65rem" }}
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: "0.8rem", maxWidth: "95%" }}>
              {link.URL}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
            <Tooltip title="Copy link">
              <IconButton size="small" onClick={() => onCopy("https://" + shortUrl)}>
                <ContentCopyRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isPremium && (
              <>
                <Tooltip title="View QR code">
                  <IconButton size="small" onClick={() => onViewQR(link)} color="primary">
                    <QrCode2RoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Manage link">
                  <IconButton size="small" onClick={() => onManage(link)} color="primary">
                    <TuneRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete link">
                  <IconButton
                    size="small"
                    onClick={() => onDelete(link)}
                    sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}
                  >
                    <DeleteRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <BarChartRoundedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
              {(link.clicks || 0).toLocaleString()} click{link.clicks !== 1 ? "s" : ""}
            </Typography>
          </Box>
          {dateStr && (
            <Typography variant="body2" color="text.disabled" fontSize="0.8rem">
              {dateStr}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Dashboard({ user, isPremium, onUpgrade, onNewLink, onCopyNotify }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState(null); // null = All
  const [editingLink, setEditingLink] = useState(null);
  const [qrLink, setQrLink] = useState(null);
  const qrRef = useRef(null);

  useEffect(() => {
    const fetchLinks = async () => {
      setLoading(true);
      try {
        const [rSnap, cSnap] = await Promise.all([
          getDocs(query(collection(db, "links"), where("uid", "==", user.uid))),
          getDocs(query(collection(db, "linksCustom"), where("uid", "==", user.uid))),
        ]);
        const all = [
          ...rSnap.docs.map((d) => ({ id: d.id, isCustom: false, ...d.data() })),
          ...cSnap.docs.map((d) => ({ id: d.id, isCustom: true, ...d.data() })),
        ].sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
        setLinks(all);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    fetchLinks();
  }, [user.uid]);

  const folders = useMemo(
    () => [...new Set(links.filter((l) => l.folder).map((l) => l.folder))].sort(),
    [links]
  );

  const displayedLinks = activeFolder
    ? links.filter((l) => l.folder === activeFolder)
    : links;

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url);
    onCopyNotify();
  };

  const handleDelete = async (link) => {
    if (!window.confirm("Delete this link? Anyone who has it will get a 'link not found' error.")) return;
    try {
      await deleteDoc(doc(db, link.isCustom ? "linksCustom" : "links", link.id));
      setLinks((prev) => prev.filter((l) => !(l.id === link.id && l.isCustom === link.isCustom)));
    } catch (e) {
      console.error(e);
    }
  };

  const handleLinkUpdated = (updatedLink) => {
    setLinks((prev) =>
      prev.map((l) =>
        l.id === editingLink.id && l.isCustom === editingLink.isCustom ? updatedLink : l
      )
    );
    setEditingLink(updatedLink);
  };

  const downloadQR = () => {
    const svgEl = qrRef.current?.querySelector("svg");
    if (!svgEl) return;
    const size = 512;
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size, size);
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, size, size);
      const a = document.createElement("a");
      a.download = `${qrLink.id}-qr.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <Box sx={{ width: { xs: "calc(100vw - 32px)", sm: 540 }, maxWidth: 540 }}>
      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" fontWeight={800}>My Links</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddLinkRoundedIcon />}
          onClick={onNewLink}
          sx={{ borderRadius: 50, textTransform: "none", fontWeight: 600 }}
        >
          New link
        </Button>
      </Box>

      {/* Folder tabs */}
      {folders.length > 0 && (
        <Tabs
          value={activeFolder ?? false}
          onChange={(_, val) => setActiveFolder(val === false ? null : val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0, textTransform: "none", fontWeight: 600, fontSize: "0.8rem" } }}
        >
          <Tab label="All" value={false} />
          {folders.map((f) => (
            <Tab key={f} label={f} value={f} icon={<FolderRoundedIcon sx={{ fontSize: 14 }} />} iconPosition="start" />
          ))}
        </Tabs>
      )}

      {/* Upgrade CTA */}
      {!isPremium && (
        <Card sx={{ bgcolor: "primary.main", borderRadius: 4, boxShadow: "none", mb: 2, cursor: "pointer" }} onClick={onUpgrade}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <WorkspacePremiumRoundedIcon sx={{ color: "#fff" }} />
              <Box>
                <Typography fontWeight={700} color="#fff" fontSize="0.9rem">Unlock custom aliases & analytics</Typography>
                <Typography color="rgba(255,255,255,0.8)" fontSize="0.8rem">Upgrade to Blinkz Pro — $13.99/yr →</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Link list */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={100} sx={{ borderRadius: 4 }} />)}
        </Box>
      ) : displayedLinks.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, px: 2, bgcolor: "background.paper", borderRadius: 4 }}>
          <AddLinkRoundedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography color="text.secondary">
            {activeFolder ? `No links in "${activeFolder}".` : "No links yet."}
          </Typography>
          {!activeFolder && (
            <Button variant="text" color="primary" onClick={onNewLink} sx={{ mt: 1, borderRadius: 50, textTransform: "none" }}>
              Create your first link
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {displayedLinks.map((link) => (
            <LinkCard
              key={link.id + (link.isCustom ? "-c" : "")}
              link={link}
              onCopy={handleCopy}
              isPremium={isPremium}
              onDelete={handleDelete}
              onManage={(l) => setEditingLink(l)}
              onViewQR={(l) => setQrLink(l)}
            />
          ))}
        </Box>
      )}

      <ManageLinkDialog
        open={!!editingLink}
        onClose={() => setEditingLink(null)}
        link={editingLink}
        existingFolders={folders}
        onUpdated={handleLinkUpdated}
        onNotify={onCopyNotify}
      />

      {/* QR Code Dialog */}
      <Dialog
        open={!!qrLink}
        onClose={() => setQrLink(null)}
        PaperProps={{ sx: { borderRadius: 5, m: 2, width: "100%", maxWidth: 340 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0 }}>QR Code</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, pt: "16px !important", pb: 3 }}>
          <Box
            ref={qrRef}
            sx={{
              p: 2.5,
              bgcolor: "#fff",
              borderRadius: 3,
              display: "inline-flex",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            }}
          >
            {qrLink && (
              <QRCodeSVG
                value={`https://${window.location.hostname}/${qrLink.id}`}
                size={200}
                fgColor="#111111"
                bgColor="#ffffff"
                level="M"
              />
            )}
          </Box>
          <Typography
            variant="body2"
            color="primary.main"
            fontWeight={600}
            sx={{ mt: -0.5 }}
          >
            {qrLink ? `${window.location.hostname}/${qrLink.id}` : ""}
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadRoundedIcon />}
            onClick={downloadQR}
            sx={{ borderRadius: 50, textTransform: "none", fontWeight: 600 }}
          >
            Download PNG
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
